import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useRef } from 'react'
import { keys } from '../../api/keys'
import { emit } from '../../platform/analytics/analytics'
import { useToast } from '../../platform/feedback/ToastProvider'
import { applyCategoryMemory } from './categoryMemory'
import type { MemorySuggestion } from './transactionWire'
import { saveFailureKind } from './useAutosaveField'

/**
 * One loop per memory across the whole app, not per hook instance: Transactions and CashflowDetail mount two
 * instances, and the server reuses one memory id per merchant.
 */
const running = new Set<string>()

export type MemorySurface = 'detail' | 'review' | 'list'

// Exported so the screens' tests import the copy instead of restating it.
export const UNAVAILABLE = 'This suggestion is no longer available'
export const offerCopy = (n: number): string => `Apply to ${n} other ${n === 1 ? 'transaction' : 'transactions'}?`
export const appliedCopy = (n: number): string => `Applied to ${n} ${n === 1 ? 'transaction' : 'transactions'}`

/**
 * The learned-category-memory offer (#753): after a single-row category edit the server may attach a
 * `memory_suggestion`, and this shows it as a toast with an Apply action. The toast is the offer and
 * dismisses itself when Apply is pressed. The loop lives in `categoryMemory.ts`.
 *
 * An apply that changed rows invalidates transactions and cashflow, the set a category change already
 * uses. Not `keys.review`: applied rows stay `pending`, so nothing leaves the queue, and `keys.ts` reserves
 * that invalidation for a mutation that changes what is pending (a refetch makes Review's rows jump).
 *
 * `withdraw` exists for Undo: the edit that taught the memory was taken back, so an Apply pressed
 * afterwards must apply nothing.
 */
export function useCategoryMemoryOffer(surface: MemorySurface): {
  /** Show "Apply to N other transactions?" for a non-null suggestion; null does nothing. */
  offer: (s: MemorySuggestion | null) => void
  /** The edit that made THIS offer was undone: an Apply pressed on it applies nothing. Keyed by the offer, not
   *  the memory id, which the server reuses for every edit of one merchant. */
  withdraw: (s: MemorySuggestion) => void
} {
  const client = useQueryClient()
  const toast = useToast()
  const withdrawn = useRef(new WeakSet<MemorySuggestion>())

  const run = useCallback(
    async (s: MemorySuggestion): Promise<void> => {
      if (withdrawn.current.has(s)) {
        toast.show({ kind: 'info', message: UNAVAILABLE })
        return
      }
      if (running.has(s.memoryId)) return
      running.add(s.memoryId)
      try {
        const out = await applyCategoryMemory(s.memoryId)
        if (out.applied > 0) {
          await Promise.all([
            client.invalidateQueries({ queryKey: keys.transactions.all() }),
            client.invalidateQueries({ queryKey: keys.cashflow.all() }),
          ])
        }
        if (out.kind === 'failed') {
          emit('category_memory_apply_failed', { error_kind: saveFailureKind(out.status), transaction_count: out.applied, surface })
          // Both resume with the same memory id: the loop is idempotent (applied rows stop matching), and the
          // id is reachable only through this toast (no read endpoint).
          toast.show({
            kind: 'error',
            message: out.applied > 0 ? `${appliedCopy(out.applied)}. Couldn’t apply the rest.` : 'Couldn’t apply the category.',
            action: { label: 'Try again', onAct: () => void run(s) },
          })
          return
        }
        if (out.kind === 'gone') emit('category_memory_unavailable', { reason_code: out.reason, surface })
        if (out.kind === 'done' || out.applied > 0) {
          emit('category_memory_applied', { transaction_count: out.applied, request_count: out.rounds, surface })
        }
        toast.show(
          out.applied > 0
            ? { kind: 'success', message: appliedCopy(out.applied) }
            : { kind: 'info', message: out.kind === 'gone' ? UNAVAILABLE : 'Nothing left to change' },
        )
      } finally {
        running.delete(s.memoryId)
      }
    },
    [client, toast, surface],
  )

  const offer = useCallback(
    (s: MemorySuggestion | null) => {
      if (s === null) return
      emit('category_memory_prompt_shown', { transaction_count: s.matchingCount, surface })
      toast.show({ kind: 'info', message: offerCopy(s.matchingCount), action: { label: 'Apply', onAct: () => void run(s) } })
    },
    [toast, surface, run],
  )

  const withdraw = useCallback((s: MemorySuggestion) => {
    withdrawn.current.add(s)
  }, [])
  return { offer, withdraw }
}
